import React, { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import toast from 'react-hot-toast'
import { showError, showSuccess, showValidationError } from '@/lib/swal'
import { PageHeader } from '@/components/shared/PageHeader'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { useAuthStore } from '@/store/auth.store'
import { payrollPaymentService, toPayrollPaymentMonthYear } from '@/services/payrollPaymentService'
import { employeesService } from '@/services/employeesService'
import type {
  PayrollPaymentSavePayload,
} from '@/api/types/payroll.types'
import type { DepartmentLookup } from '@/api/types'

function getDefaultMonthYear(): string {
  const d = new Date()
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
}

export const PayrollPaymentDetail: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const branchId = user?.selectedBranch?.branchId ?? 0

  const [departments, setDepartments] = useState<DepartmentLookup[]>([])
  const [payrollMonthYear, setPayrollMonthYear] = useState(getDefaultMonthYear())
  const [departmentId, setDepartmentId] = useState(0)
  const [paymentDate, setPaymentDate] = useState(() => {
    const d = new Date()
    return d.toISOString().slice(0, 10)
  })
  const [saving, setSaving] = useState(false)
  const [checkingRemaining, setCheckingRemaining] = useState(false)
  const [saveDisabled, setSaveDisabled] = useState(false)
  const lastAlertedKeyRef = useRef<string>('')
  const lastNoRemainingRef = useRef<boolean>(false)

  // Load departments
  useEffect(() => {
    let cancelled = false
    employeesService.getDepartmentLookups().then((list) => {
      if (!cancelled && list) setDepartments(list)
    }).catch(() => { if (!cancelled) setDepartments([]) })
    return () => { cancelled = true }
  }, [])

  // Auto-check remaining payment when Month/Year or Department changes (Department "All" = 0 is allowed)
  useEffect(() => {
    if (!branchId) {
      setSaveDisabled(false)
      return
    }
    if (!payrollMonthYear || payrollMonthYear.length < 7) {
      setSaveDisabled(false)
      return
    }

    const key = `${branchId}|${departmentId}|${payrollMonthYear}`
    let cancelled = false
    setCheckingRemaining(true)

    payrollPaymentService
      .isAnyPaymentRemaining(branchId, departmentId, payrollMonthYear)
      .then(async (remaining) => {
        if (cancelled) return

        const noRemaining =
          !remaining?.isAnyPaymentRemaining ||
          Number(remaining?.remainingAmount ?? 0) <= 0 ||
          Number(remaining?.remainingEmployees ?? 0) <= 0

        setSaveDisabled(noRemaining)

        // Avoid spamming the same Swal repeatedly while user is on same selection.
        if (noRemaining && (!lastNoRemainingRef.current || lastAlertedKeyRef.current !== key)) {
          lastNoRemainingRef.current = true
          lastAlertedKeyRef.current = key
          await showError(
            t('payrollPaymentPage.paymentAlreadyDoneTitle'),
            t('payrollPaymentPage.paymentAlreadyDoneMessage')
          )
        }

        if (!noRemaining) {
          lastNoRemainingRef.current = false
        }
      })
      .catch(() => {
        if (cancelled) return
        // If check fails, don't block saving (backend will validate on save anyway).
        setSaveDisabled(false)
      })
      .finally(() => {
        if (!cancelled) setCheckingRemaining(false)
      })

    return () => {
      cancelled = true
    }
  }, [branchId, departmentId, payrollMonthYear, t])

  const departmentOptions = useMemo(
    () => [
      { value: '0', label: t('payrollPaymentPage.placeholderAll') },
      ...departments.filter((d) => d.id).map((d) => ({
        value: String(d.id),
        label: d.lookupNameEn || d.lookupNameAr || String(d.id),
      })),
    ],
    [departments, t]
  )

  const handleSave = async () => {
    if (!branchId) {
      await showValidationError(t('payrollPaymentPage.validationBranch'))
      return
    }
    if (!payrollMonthYear || payrollMonthYear.length < 7) {
      await showValidationError(t('payrollPaymentPage.validationMonthYear'))
      return
    }
    if (!paymentDate || paymentDate.length < 10) {
      await showValidationError(t('payrollPaymentPage.validationPaymentDate'))
      return
    }
    if (saveDisabled) {
      await showError(
        t('payrollPaymentPage.paymentAlreadyDoneTitle'),
        t('payrollPaymentPage.paymentAlreadyDoneMessage')
      )
      return
    }

    setSaving(true)
    try {
      const apiMonthYear = toPayrollPaymentMonthYear(payrollMonthYear)
      const payload: PayrollPaymentSavePayload = {
        payrollMonthYear: apiMonthYear,
        paymentDate: new Date(paymentDate + 'T00:00:00').toISOString(),
        branchId,
        departmentId,
      }
      console.log('Payroll Payment (Save) Payload:', JSON.stringify(payload, null, 2))

      await payrollPaymentService.save(payload)
      await showSuccess(t('payrollPaymentPage.savedTitle'), t('payrollPaymentPage.savedMessage'))
      navigate('/payroll/payroll-payment')
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('payrollPaymentPage.saveFailed'))
    } finally {
      setSaving(false)
    }
  }

  const req = t('payrollPaymentPage.requiredMark')

  if (!branchId) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.formDescription')} />
        <Card>
          <CardContent className="py-8 text-center text-muted-foreground">
            {t('payrollPaymentPage.selectBranchForm')}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader title={t('payrollPaymentPage.pageTitle')} description={t('payrollPaymentPage.formDescription')} />

      <Card>
        <CardHeader>
          <CardTitle>{t('payrollPaymentPage.addTitle')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>{req} {t('payrollPaymentPage.labelMonthYear')}</Label>
              <Input
                type="month"
                value={payrollMonthYear}
                onChange={(e) => setPayrollMonthYear(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('payrollPaymentPage.labelDepartment')}</Label>
              <SearchableSelect
                options={departmentOptions}
                value={departmentId ? String(departmentId) : '0'}
                onValueChange={(v) => setDepartmentId(v ? Number(v) : 0)}
                placeholder={t('payrollPaymentPage.placeholderAll')}
                searchPlaceholder={t('payrollPaymentPage.searchShortPlaceholder')}
                emptyMessage={t('payrollPaymentPage.emptyDepartment')}
              />
            </div>
            <div className="space-y-2">
              <Label>{req} {t('payrollPaymentPage.labelPaymentDate')}</Label>
              <Input
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => navigate('/payroll/payroll-payment')}>
              {t('payrollPaymentPage.cancel')}
            </Button>
            <Button onClick={handleSave} disabled={saving || checkingRemaining || saveDisabled}>
              {saving || checkingRemaining ? t('payrollPaymentPage.saving') : t('payrollPaymentPage.save')}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
